import { Table } from "@digdir/designsystemet-react";
import { Fragment, type ReactNode } from "react";
import type { ReportCatalogEntry, ReportDetailField } from "../../data/types";
import type { SortDirection, SortOption } from "../../filter";
import { RapportkatalogCardDetails } from "../card";
import "./RapportkatalogTable.css";

type RapportkatalogTableProps = {
    rows: ReportCatalogEntry[];
    isLastChangedField: (field: ReportDetailField) => boolean;
    renderDetailValue: (value: string, label?: string) => ReactNode;
    renderLastChangedValue: (value?: string) => ReactNode;
    sortOption: SortOption;
    sortDirection: SortDirection;
    onSortHeaderClick: (sortOption: SortOption) => void;
};

export function RapportkatalogTable({
    rows,
    isLastChangedField,
    renderDetailValue,
    renderLastChangedValue,
    sortOption,
    sortDirection,
    onSortHeaderClick,
}: RapportkatalogTableProps) {
    const getHeaderSort = (headerSortOption: SortOption): "ascending" | "descending" | "none" => {
        if (sortOption !== headerSortOption) {
            return "none";
        }

        return sortDirection === "asc" ? "ascending" : "descending";
    };

    return (
        <Table className="rapportkatalog-responsive-table" border>
            <caption className="sr-only">Tabell med rapporter i rapportkatalogen</caption>
            <Table.Head>
                <Table.Row>
                    <Table.HeaderCell sort={getHeaderSort("title")} onClick={() => onSortHeaderClick("title")}>
                        Tittel
                    </Table.HeaderCell>
                    <Table.HeaderCell sort={getHeaderSort("area")} onClick={() => onSortHeaderClick("area")}>
                        Område
                    </Table.HeaderCell>
                    <Table.HeaderCell sort={getHeaderSort("owner")} onClick={() => onSortHeaderClick("owner")}>
                        Eier
                    </Table.HeaderCell>
                    <Table.HeaderCell
                        sort={getHeaderSort("lastChanged")}
                        onClick={() => onSortHeaderClick("lastChanged")}
                    >
                        Sist oppdatert
                    </Table.HeaderCell>
                    <Table.HeaderCell>Kort om rapporten</Table.HeaderCell>
                    <Table.HeaderCell
                        sort={getHeaderSort("targetGroups")}
                        onClick={() => onSortHeaderClick("targetGroups")}
                    >
                        Målgruppe
                    </Table.HeaderCell>
                </Table.Row>
            </Table.Head>
            <Table.Body>
                {rows.map((row) => {
                    const lastChangedField = row.detailFields.find((field) => isLastChangedField(field));
                    const detailFieldsForTable = row.detailFields.filter((field) => !isLastChangedField(field));

                    return (
                        <Fragment key={row.id}>
                            <Table.Row>
                                <Table.Cell data-label="Tittel" style={{ borderBottom: "none" }}>
                                    {row.title || "-"}
                                </Table.Cell>
                                <Table.Cell data-label="Område" style={{ borderBottom: "none" }}>
                                    {row.area || "-"}
                                </Table.Cell>
                                <Table.Cell data-label="Eier" style={{ borderBottom: "none" }}>
                                    {row.owner || "-"}
                                </Table.Cell>
                                <Table.Cell data-label="Sist oppdatert" style={{ borderBottom: "none" }}>
                                    {renderLastChangedValue(lastChangedField?.value)}
                                </Table.Cell>
                                <Table.Cell data-label="Kort om rapporten" style={{ borderBottom: "none" }}>
                                    {row.shortDescription || "-"}
                                </Table.Cell>
                                <Table.Cell data-label="Målgruppe" style={{ borderBottom: "none" }}>
                                    {row.targetGroups || "-"}
                                </Table.Cell>
                            </Table.Row>

                            <Table.Row>
                                <Table.Cell colSpan={6} data-label="Flere detaljer">
                                    <RapportkatalogCardDetails
                                        detailFields={detailFieldsForTable}
                                        renderDetailValue={renderDetailValue}
                                    />
                                </Table.Cell>
                            </Table.Row>
                        </Fragment>
                    );
                })}
            </Table.Body>
        </Table>
    );
}
